import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import DatePickerField from "./DatePickerField";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "./ui/command";
import { Receipt, Plus, Trash2, Search, ChevronDown, Camera, UserPlus, DollarSign, CheckCircle2, X, Image as ImageIcon } from "lucide-react";
import { useData } from "../contexts/DataContext";
import PhotoUpload from "./PhotoUpload";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const emptyItem = () => ({ description: "", quantity: 1, unit_price: "", price_list_id: "" });

const newForm = (contactId) => ({
  contact_id: contactId || "",
  title: "",
  description: "",
  due_date: "",
  invoice_type: "custom",
  line_items: [emptyItem()],
});

// Compute the customer-facing unit price for a price-list item:
//   base_price * (1 + per-item margin %)
// Mirrors how Quote line items render so numbers stay consistent.
const computeUnitPrice = (pl) => {
  const base = parseFloat(pl?.base_price) || 0;
  const margin = parseFloat(pl?.margin) || 0;
  return Math.round(base * (1 + margin / 100) * 100) / 100;
};

function PriceListPicker({ value, onSelect, priceList }) {
  const [open, setOpen] = useState(false);
  const enabled = (priceList || []).filter(p => p.enabled !== false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center justify-between gap-1 px-2 h-9 rounded-md border border-slate-200 bg-white hover:bg-slate-50 text-[11px] text-slate-500 hover:text-[#0F4C81] transition-colors flex-shrink-0"
          title="Pick from your Price List"
          data-testid="qi-pricelist-trigger"
        >
          <Search className="w-3 h-3" />
          <ChevronDown className="w-3 h-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[360px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search price list..." className="h-9" />
          <CommandList>
            <CommandEmpty>No items found</CommandEmpty>
            <CommandGroup>
              {enabled.map(pl => {
                const price = computeUnitPrice(pl);
                return (
                  <CommandItem
                    key={pl.id}
                    value={`${pl.name} ${pl.description || ""} ${pl.category || ""}`}
                    onSelect={() => {
                      onSelect(pl);
                      setOpen(false);
                    }}
                    className="flex items-center justify-between gap-2 py-2"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{pl.name}</p>
                      <p className="text-[10px] text-slate-400 uppercase">{pl.category}{pl.unit ? ` · per ${pl.unit}` : ""}</p>
                    </div>
                    <span className="text-sm font-semibold text-[#0F4C81] flex-shrink-0">${price.toFixed(2)}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Inline contact picker — search existing OR create a new contact without
 * leaving the invoice modal. Returns the selected/created contact id.
 */
function ContactPicker({ value, onChange, contacts, onContactCreated }) {
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ name: "", phone: "", email: "" });
  const [saving, setSaving] = useState(false);
  const { createContact } = useData();
  const selected = (contacts || []).find(c => c.id === value);

  const submitNew = async () => {
    if (!draft.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      const created = await createContact({
        name: draft.name.trim(),
        phone: draft.phone.trim(),
        email: draft.email.trim(),
      });
      onContactCreated?.(created);
      onChange(created.id);
      toast.success(`${created.name} added`);
      setDraft({ name: "", phone: "", email: "" });
      setCreating(false);
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create contact");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setCreating(false); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center justify-between w-full h-11 px-3 rounded-md border border-slate-200 bg-white hover:bg-slate-50 text-sm transition-colors"
          data-testid="qi-contact-picker"
        >
          <span className={selected ? "text-slate-800" : "text-slate-400"}>
            {selected ? `${selected.name}${selected.email ? ` — ${selected.email}` : ""}` : "Select or create a contact"}
          </span>
          <ChevronDown className="w-4 h-4 text-slate-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        {creating ? (
          <div className="p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-700">New contact</span>
              <button type="button" onClick={() => setCreating(false)} className="text-slate-400 hover:text-slate-600" data-testid="qi-newcontact-cancel">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <Input
              autoFocus
              placeholder="Name *"
              value={draft.name}
              onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
              className="h-9 text-sm"
              data-testid="qi-newcontact-name"
            />
            <Input
              placeholder="Phone"
              value={draft.phone}
              onChange={e => setDraft(d => ({ ...d, phone: e.target.value }))}
              className="h-9 text-sm"
              data-testid="qi-newcontact-phone"
            />
            <Input
              placeholder="Email"
              type="email"
              value={draft.email}
              onChange={e => setDraft(d => ({ ...d, email: e.target.value }))}
              className="h-9 text-sm"
              data-testid="qi-newcontact-email"
            />
            <Button
              type="button"
              size="sm"
              disabled={saving || !draft.name.trim()}
              onClick={submitNew}
              className="w-full h-9 bg-[#0F4C81] hover:bg-[#0D4270] text-xs"
              data-testid="qi-newcontact-save"
            >
              {saving ? "Saving…" : "Add contact"}
            </Button>
          </div>
        ) : (
          <Command>
            <CommandInput placeholder="Search contacts..." className="h-9" />
            <CommandList>
              <CommandEmpty>No contacts found</CommandEmpty>
              <CommandGroup>
                <CommandItem
                  value="__new__"
                  onSelect={() => setCreating(true)}
                  className="text-[#0F4C81] font-medium"
                  data-testid="qi-newcontact-trigger"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Create new contact
                </CommandItem>
                {(contacts || []).map(c => (
                  <CommandItem
                    key={c.id}
                    value={`${c.name} ${c.email || ""} ${c.phone || ""}`}
                    onSelect={() => { onChange(c.id); setOpen(false); }}
                  >
                    <div className="flex flex-col min-w-0">
                      <span className="text-sm font-medium truncate">{c.name}</span>
                      {(c.email || c.phone) && (
                        <span className="text-[10px] text-slate-400 truncate">{c.email || c.phone}</span>
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        )}
      </PopoverContent>
    </Popover>
  );
}

/**
 * Standalone "Quick Invoice" modal — bill a contact without creating a project.
 * Supports line items + autocomplete picker that pulls from your Price List.
 *
 * Props:
 *   editInvoice (optional): existing invoice object → puts modal in edit mode
 */
export default function QuickInvoiceModal({ open, onOpenChange, contactId, contacts, editInvoice, onCreated }) {
  const { priceList, fetchPriceList } = useData();
  const [form, setForm] = useState(newForm(contactId));
  const [submitting, setSubmitting] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [payments, setPayments] = useState([]);
  const [invoiceState, setInvoiceState] = useState(editInvoice); // tracks live status/paid after recording
  // Pre-fill date with today so admins always see (and can override) the
  // payment date — including when they click the "Mark fully paid"
  // quick-action which otherwise skips the date field entirely.
  const [paymentDraft, setPaymentDraft] = useState({
    amount: "",
    method: "check",
    date: new Date().toISOString().slice(0, 10),
    reference: "",
    notes: "",
  });
  const [recording, setRecording] = useState(false);
  const [pullOpen, setPullOpen] = useState(false);
  const [projectPhotos, setProjectPhotos] = useState([]);
  const [pullSelected, setPullSelected] = useState({}); // id -> bool
  const [pulling, setPulling] = useState(false);
  const isEdit = !!editInvoice;
  const projectId = editInvoice?.project_id || "";

  useEffect(() => {
    if (open) {
      if (editInvoice) {
        // Hydrate form from the existing invoice
        const items = (editInvoice.line_items && editInvoice.line_items.length > 0)
          ? editInvoice.line_items.map(it => ({
              description: it.description || "",
              quantity: parseFloat(it.quantity) || 1,
              unit_price: parseFloat(it.unit_price) || 0,
              price_list_id: it.price_list_id || "",
            }))
          : [{ description: editInvoice.title || "Item", quantity: 1, unit_price: parseFloat(editInvoice.amount) || 0, price_list_id: "" }];
        setForm({
          contact_id: editInvoice.contact_id || contactId || "",
          title: editInvoice.title || "",
          description: editInvoice.description || "",
          due_date: editInvoice.due_date || "",
          invoice_type: editInvoice.invoice_type || "custom",
          line_items: items,
        });
      } else {
        setForm(newForm(contactId));
      }
      fetchPriceList?.();
      // Load photos for this invoice when editing
      if (editInvoice?.id) {
        axios.get(`${API_URL}/uploads/entity/invoice/${editInvoice.id}`)
          .then(res => setPhotos(res.data || []))
          .catch(() => setPhotos([]));
        axios.get(`${API_URL}/payments`, { params: { invoice_id: editInvoice.id } })
          .then(res => setPayments(res.data || []))
          .catch(() => setPayments([]));
        setInvoiceState(editInvoice);
        setPaymentDraft({
          amount: "",
          method: "check",
          date: new Date().toISOString().slice(0, 10),
          reference: "",
          notes: "",
        });
      } else {
        setPhotos([]);
        setPayments([]);
        setInvoiceState(null);
      }
    }
  }, [open, contactId, editInvoice, fetchPriceList]);

  const updateItem = (idx, key, value) => {
    setForm(prev => ({
      ...prev,
      line_items: prev.line_items.map((it, i) => i === idx ? { ...it, [key]: value } : it),
    }));
  };
  const pickPriceListItem = (idx, pl) => {
    setForm(prev => ({
      ...prev,
      line_items: prev.line_items.map((it, i) => i !== idx ? it : {
        ...it,
        price_list_id: pl.id,
        description: pl.name + (pl.unit ? ` (per ${pl.unit})` : ""),
        unit_price: computeUnitPrice(pl),
        quantity: parseFloat(it.quantity) > 0 ? it.quantity : 1,
      }),
    }));
  };
  const addItem = () => setForm(p => ({ ...p, line_items: [...p.line_items, emptyItem()] }));
  const removeItem = (idx) => setForm(p => ({
    ...p,
    line_items: p.line_items.length > 1 ? p.line_items.filter((_, i) => i !== idx) : p.line_items,
  }));

  const total = form.line_items.reduce((sum, it) => {
    const q = parseFloat(it.quantity) || 0;
    const u = parseFloat(it.unit_price) || 0;
    return sum + q * u;
  }, 0);

  // ------- Payments (edit-mode only) -------
  const invoiceAmount = parseFloat(invoiceState?.amount) || 0;
  const totalPaid = payments.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
  const balanceDue = Math.max(invoiceAmount - totalPaid, 0);

  const refreshAfterPayment = async () => {
    if (!editInvoice?.id) return;
    try {
      const [pmt, inv] = await Promise.all([
        axios.get(`${API_URL}/payments`, { params: { invoice_id: editInvoice.id } }),
        axios.get(`${API_URL}/invoices/${editInvoice.id}`),
      ]);
      setPayments(pmt.data || []);
      setInvoiceState(inv.data || invoiceState);
      onCreated?.(inv.data);
    } catch {}
  };

  const recordPayment = async (amountOverride) => {
    const amt = parseFloat(amountOverride ?? paymentDraft.amount) || 0;
    if (amt <= 0) {
      toast.error("Enter a payment amount");
      return;
    }
    setRecording(true);
    try {
      await axios.post(`${API_URL}/payments`, {
        invoice_id: editInvoice.id,
        contact_id: invoiceState?.contact_id || "",
        project_id: invoiceState?.project_id || "",
        amount: amt,
        method: paymentDraft.method || "check",
        date: paymentDraft.date || "",
        reference: paymentDraft.reference || "",
        notes: paymentDraft.notes || "",
      });
      await refreshAfterPayment();
      setPaymentDraft({
        amount: "",
        method: "check",
        date: new Date().toISOString().slice(0, 10),
        reference: "",
        notes: "",
      });
      toast.success(`Payment of $${amt.toFixed(2)} recorded`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to record payment");
    } finally {
      setRecording(false);
    }
  };

  const markFullyPaid = () => {
    if (balanceDue <= 0) {
      toast.info("Already fully paid");
      return;
    }
    recordPayment(balanceDue);
  };

  const deletePayment = async (paymentId) => {
    if (!window.confirm("Delete this payment? The invoice status will recalculate.")) return;
    try {
      await axios.delete(`${API_URL}/payments/${paymentId}`);
      await refreshAfterPayment();
      toast.success("Payment deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete payment");
    }
  };

  // ------- Pull from Project Photos -------
  const openPullDialog = async () => {
    if (!projectId) return;
    try {
      const res = await axios.get(`${API_URL}/uploads/entity/project/${projectId}`);
      setProjectPhotos(res.data || []);
      setPullSelected({});
      setPullOpen(true);
    } catch {
      toast.error("Failed to load project photos");
    }
  };

  const togglePullSelected = (id) =>
    setPullSelected(prev => ({ ...prev, [id]: !prev[id] }));

  const confirmPullPhotos = async () => {
    const ids = Object.keys(pullSelected).filter(id => pullSelected[id]);
    if (ids.length === 0) {
      toast.error("Select at least one photo");
      return;
    }
    setPulling(true);
    try {
      const res = await axios.post(`${API_URL}/uploads/copy`, {
        source_ids: ids,
        target_entity_type: "invoice",
        target_entity_id: editInvoice.id,
      });
      const copied = res.data?.files || [];
      setPhotos(prev => [...prev, ...copied]);
      toast.success(`${copied.length} photo${copied.length === 1 ? "" : "s"} linked from project`);
      setPullOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to copy photos");
    } finally {
      setPulling(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.contact_id && !projectId) {
      toast.error("Please select a contact");
      return;
    }
    const items = form.line_items
      .filter(it => (it.description || "").trim() || parseFloat(it.unit_price) > 0)
      .map(it => ({
        price_list_id: it.price_list_id || "",
        description: (it.description || "").trim() || "Item",
        quantity: parseFloat(it.quantity) || 0,
        unit_price: parseFloat(it.unit_price) || 0,
        amount: (parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0),
      }));
    if (items.length === 0 || total <= 0) {
      toast.error("Add at least one line item with an amount");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        contact_id: form.contact_id,
        invoice_type: form.invoice_type,
        title: form.title || "Invoice",
        description: form.description,
        amount: Math.round(total * 100) / 100,
        line_items: items,
        due_date: form.due_date,
      };
      let res;
      if (isEdit) {
        res = await axios.put(`${API_URL}/invoices/${editInvoice.id}`, payload);
        toast.success(`Invoice ${editInvoice.invoice_number || ""} updated`);
      } else {
        res = await axios.post(`${API_URL}/invoices`, payload);
        toast.success(`Invoice ${res.data.invoice_number} created`);
      }
      onCreated?.(res.data);
      onOpenChange(false);
    } catch (err) {
      const msg = err?.response?.data?.detail || (isEdit ? "Failed to update invoice" : "Failed to create invoice");
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-1.5rem)] sm:max-w-4xl max-h-[90vh] overflow-y-auto overflow-x-hidden p-4 sm:p-6" data-testid="quick-invoice-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="w-5 h-5 text-[#0F4C81]" />
            {isEdit ? `Edit Invoice ${editInvoice?.invoice_number || ""}` : "Quick Invoice"}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update line items, amount, or notes. Changes are saved to the server immediately."
              : "Bill a contact directly — no project required. Add line items to break out fees, parts, and labor."}
          </DialogDescription>
        </DialogHeader>
        {isEdit && (editInvoice?.status === "paid" || editInvoice?.status === "partial") && (
          <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-xs text-amber-800 flex items-start gap-2">
            <span className="font-bold">⚠</span>
            <span>
              This invoice has been <b>{editInvoice.status === "paid" ? "paid" : "partially paid"}</b>
              {editInvoice.status === "partial" ? ` (${(editInvoice.paid_amount || 0).toLocaleString(undefined, { style: "currency", currency: "USD" })} received)` : ""}.
              Edits here update the invoice record only — they do <b>not</b> refund the customer or re-charge the card. Make sure to send the updated copy if needed.
            </span>
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          {!contactId && !projectId && (
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Contact *</Label>
              <ContactPicker
                value={form.contact_id}
                onChange={(v) => setForm(p => ({ ...p, contact_id: v }))}
                contacts={contacts}
              />
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Title</Label>
              <Input
                value={form.title}
                onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                placeholder="e.g., Service Call — Roof Inspection"
                className="h-11"
                data-testid="qi-title"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Due Date</Label>
              <DatePickerField
                value={form.due_date}
                onChange={(v) => setForm(p => ({ ...p, due_date: v }))}
                testId="qi-due-date"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Description / Notes</Label>
            <Textarea
              value={form.description}
              onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
              rows={2}
              placeholder="Optional notes about this invoice (shown to customer)"
              data-testid="qi-description"
            />
          </div>

          {/* Line items */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Line Items</Label>
              <Button type="button" size="sm" variant="ghost" onClick={addItem} className="h-7 text-xs text-[#0F4C81]" data-testid="qi-add-item">
                <Plus className="w-3 h-3 mr-1" /> Add Item
              </Button>
            </div>
            <div className="border rounded-md overflow-hidden">
              {/* Header (md+ only) — fixed widths for trailing columns guarantee Amount + delete always fit */}
              <div className="hidden md:grid grid-cols-[1fr_48px_88px_88px] gap-2 px-3 py-1.5 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                <div className="min-w-0">Description</div>
                <div className="text-center">Qty</div>
                <div className="text-right">Unit Price</div>
                <div className="text-right pr-6">Amount</div>
              </div>
              {form.line_items.map((it, idx) => {
                const q = parseFloat(it.quantity) || 0;
                const u = parseFloat(it.unit_price) || 0;
                const amt = q * u;
                return (
                  <div key={idx} className="border-t first:border-t-0 md:first:border-t" data-testid={`qi-row-${idx}`}>
                    {/* Desktop row */}
                    <div className="hidden md:grid grid-cols-[1fr_48px_88px_88px] gap-2 px-3 py-2 items-center">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <PriceListPicker
                          value={it.price_list_id}
                          onSelect={(pl) => pickPriceListItem(idx, pl)}
                          priceList={priceList}
                        />
                        <Input
                          value={it.description}
                          onChange={e => updateItem(idx, "description", e.target.value)}
                          placeholder="e.g., Diagnostic fee"
                          className="flex-1 min-w-0 h-9 text-sm"
                          data-testid={`qi-item-desc-${idx}`}
                        />
                      </div>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={it.quantity ?? ""}
                        onChange={e => updateItem(idx, "quantity", e.target.value === "" ? "" : parseFloat(e.target.value))}
                        className="no-spin min-w-0 h-9 text-sm text-center px-1"
                        data-testid={`qi-item-qty-${idx}`}
                      />
                      <div className="min-w-0 relative">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">$</span>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={it.unit_price ?? ""}
                          onChange={e => updateItem(idx, "unit_price", e.target.value === "" ? "" : parseFloat(e.target.value))}
                          className="no-spin h-9 text-sm pl-5 pr-1 text-right"
                          data-testid={`qi-item-price-${idx}`}
                        />
                      </div>
                      <div className="min-w-0 flex items-center justify-end gap-1">
                        <span className="text-sm font-semibold text-slate-700 truncate">${amt.toFixed(2)}</span>
                        <button
                          type="button"
                          onClick={() => removeItem(idx)}
                          disabled={form.line_items.length === 1}
                          className="text-slate-300 hover:text-red-500 disabled:opacity-30 disabled:hover:text-slate-300 p-1 flex-shrink-0"
                          title={form.line_items.length === 1 ? "At least one item required" : "Remove this item"}
                          data-testid={`qi-item-remove-${idx}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                    {/* Mobile row: stacked */}
                    <div className="md:hidden px-3 py-3 space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <span className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Item {idx + 1}</span>
                        <button
                          type="button"
                          onClick={() => removeItem(idx)}
                          disabled={form.line_items.length === 1}
                          className="text-slate-300 hover:text-red-500 disabled:opacity-30 disabled:hover:text-slate-300 p-1 -mt-1"
                          title={form.line_items.length === 1 ? "At least one item required" : "Remove this item"}
                          data-testid={`qi-item-remove-mobile-${idx}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <PriceListPicker
                          value={it.price_list_id}
                          onSelect={(pl) => pickPriceListItem(idx, pl)}
                          priceList={priceList}
                        />
                        <Input
                          value={it.description}
                          onChange={e => updateItem(idx, "description", e.target.value)}
                          placeholder="Description (e.g., Diagnostic fee)"
                          className="flex-1 h-10 text-sm"
                        />
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Qty</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            inputMode="decimal"
                            value={it.quantity ?? ""}
                            onChange={e => updateItem(idx, "quantity", e.target.value === "" ? "" : parseFloat(e.target.value))}
                            className="h-10 text-sm text-center mt-0.5"
                          />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Unit $</Label>
                          <div className="relative mt-0.5">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">$</span>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              inputMode="decimal"
                              value={it.unit_price ?? ""}
                              onChange={e => updateItem(idx, "unit_price", e.target.value === "" ? "" : parseFloat(e.target.value))}
                              className="h-10 text-sm pl-5 text-right"
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Amount</Label>
                          <div className="h-10 mt-0.5 flex items-center justify-end pr-2 text-base font-bold text-slate-800">${amt.toFixed(2)}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div className="flex justify-between items-center px-3 py-2.5 bg-slate-50 border-t">
                <span className="text-sm font-semibold text-slate-600">Total</span>
                <span className="text-xl font-extrabold text-[#0F4C81]" data-testid="qi-total">${total.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Payments — only when editing an existing invoice */}
          {isEdit && editInvoice?.id && (
            <div className="space-y-2" data-testid="qi-payments-section">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-emerald-600" />
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Payments {payments.length > 0 && <span className="text-slate-400 ml-1">({payments.length})</span>}
                  </Label>
                </div>
                {balanceDue > 0 ? (
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
                      Dated {paymentDraft.date || "today"}
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={markFullyPaid}
                      disabled={recording}
                      className="h-7 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                      data-testid="qi-mark-fully-paid"
                    >
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Mark fully paid (${balanceDue.toFixed(2)})
                    </Button>
                  </div>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-semibold uppercase tracking-wider" data-testid="qi-paid-in-full">
                    <CheckCircle2 className="w-3 h-3" /> Paid in full
                  </span>
                )}
              </div>

              <div className="grid grid-cols-3 gap-2 text-center text-xs">
                <div className="bg-slate-50 rounded p-2">
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Invoice</p>
                  <p className="text-sm font-bold text-slate-700">${invoiceAmount.toFixed(2)}</p>
                </div>
                <div className="bg-emerald-50 rounded p-2">
                  <p className="text-[10px] uppercase tracking-wider text-emerald-600 font-bold">Paid</p>
                  <p className="text-sm font-bold text-emerald-700" data-testid="qi-total-paid">${totalPaid.toFixed(2)}</p>
                </div>
                <div className={`${balanceDue > 0 ? "bg-amber-50" : "bg-slate-50"} rounded p-2`}>
                  <p className={`text-[10px] uppercase tracking-wider font-bold ${balanceDue > 0 ? "text-amber-600" : "text-slate-400"}`}>Balance</p>
                  <p className={`text-sm font-bold ${balanceDue > 0 ? "text-amber-700" : "text-slate-500"}`} data-testid="qi-balance-due">${balanceDue.toFixed(2)}</p>
                </div>
              </div>

              {payments.length > 0 && (
                <div className="border rounded-md divide-y divide-slate-100">
                  {payments.map(p => (
                    <div key={p.id} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`qi-payment-row-${p.id}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="font-semibold text-emerald-700">${(parseFloat(p.amount) || 0).toFixed(2)}</span>
                        <span className="text-xs text-slate-500 capitalize">{p.method || "check"}</span>
                        {p.reference && <span className="text-xs text-slate-400 truncate">#{p.reference}</span>}
                        {p.date && <span className="text-xs text-slate-400">{p.date}</span>}
                      </div>
                      <button
                        type="button"
                        onClick={() => deletePayment(p.id)}
                        className="text-slate-300 hover:text-red-500 p-1"
                        title="Delete payment"
                        data-testid={`qi-payment-delete-${p.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add payment form */}
              <div className="border border-dashed border-slate-300 rounded-md p-3 space-y-2 bg-slate-50/60">
                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Record a payment</p>
                <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
                  <div className="sm:col-span-2 relative">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">$</span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      inputMode="decimal"
                      value={paymentDraft.amount}
                      onChange={e => setPaymentDraft(d => ({ ...d, amount: e.target.value }))}
                      placeholder={`Amount (max $${balanceDue.toFixed(2)})`}
                      className="h-9 text-sm pl-5"
                      data-testid="qi-payment-amount"
                    />
                  </div>
                  <Select value={paymentDraft.method} onValueChange={(v) => setPaymentDraft(d => ({ ...d, method: v }))}>
                    <SelectTrigger className="h-9 text-sm" data-testid="qi-payment-method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="check">Check</SelectItem>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="card">Card</SelectItem>
                      <SelectItem value="transfer">Transfer</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  <DatePickerField
                    value={paymentDraft.date}
                    onChange={(v) => setPaymentDraft(d => ({ ...d, date: v }))}
                    testId="qi-payment-date"
                    height="h-9"
                  />
                  <Button
                    type="button"
                    onClick={() => recordPayment()}
                    disabled={recording || !(parseFloat(paymentDraft.amount) > 0)}
                    className="h-9 bg-emerald-600 hover:bg-emerald-700 text-xs"
                    data-testid="qi-payment-save"
                  >
                    {recording ? "Saving…" : "Add"}
                  </Button>
                </div>
                <Input
                  value={paymentDraft.reference}
                  onChange={e => setPaymentDraft(d => ({ ...d, reference: e.target.value }))}
                  placeholder="Reference / check # (optional)"
                  className="h-9 text-sm"
                  data-testid="qi-payment-reference"
                />
              </div>
            </div>
          )}

          {/* Photos — only when editing an existing invoice (need an entity_id) */}
          {isEdit && editInvoice?.id && (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <Camera className="w-4 h-4 text-[#0F4C81]" />
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Photos {photos.length > 0 && <span className="text-slate-400 ml-1">({photos.length})</span>}
                  </Label>
                </div>
                {projectId && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={openPullDialog}
                    className="h-7 text-xs border-[#0F4C81]/30 text-[#0F4C81] hover:bg-[#0F4C81]/10"
                    data-testid="qi-pull-project-photos"
                  >
                    <ImageIcon className="w-3 h-3 mr-1" />
                    Pull from project
                  </Button>
                )}
              </div>
              <p className="text-[11px] text-slate-500 -mt-1">
                Attach before/after photos, completed-work shots, or material receipts. Add a caption to each — it shows on the PDF and the customer&apos;s invoice page.
              </p>
              <PhotoUpload
                entityType="invoice"
                entityId={editInvoice.id}
                photos={photos}
                onPhotosChange={setPhotos}
                maxFiles={20}
                showCaptions={true}
              />
            </div>
          )}
          {!isEdit && (
            <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-xs text-slate-500 flex items-start gap-2">
              <Camera className="w-3.5 h-3.5 mt-0.5 text-slate-400" />
              <span>Create the invoice first, then re-open it to attach photos.</span>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" className="flex-1 h-11" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting} className="flex-1 h-11 bg-[#0F4C81] hover:bg-[#0D4270]" data-testid="qi-submit">
              {submitting ? "Saving…" : isEdit ? `Save Changes — $${total.toFixed(2)}` : `Create Invoice — $${total.toFixed(2)}`}
            </Button>
          </div>
        </form>

        {/* Pull from project photos dialog */}
        <Dialog open={pullOpen} onOpenChange={setPullOpen}>
          <DialogContent className="w-[calc(100vw-1.5rem)] sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="qi-pull-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ImageIcon className="w-5 h-5 text-[#0F4C81]" />
                Pull photos from project
              </DialogTitle>
              <DialogDescription>
                Select photos from this project to attach to the invoice. The originals stay on the project — these are copies linked to the invoice.
              </DialogDescription>
            </DialogHeader>
            {projectPhotos.length === 0 ? (
              <div className="text-center py-10 text-sm text-slate-400">
                No photos uploaded to this project yet.
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 py-2">
                {projectPhotos.map(p => {
                  const isSel = !!pullSelected[p.id];
                  const fullUrl = p.url?.startsWith("http") ? p.url : `${process.env.REACT_APP_BACKEND_URL}${p.url}`;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => togglePullSelected(p.id)}
                      className={`relative rounded-lg overflow-hidden border-2 transition-all ${isSel ? "border-[#0F4C81] ring-2 ring-[#0F4C81]/30" : "border-slate-200 hover:border-slate-300"}`}
                      data-testid={`qi-pull-photo-${p.id}`}
                    >
                      <img src={fullUrl} alt={p.original_name || ""} className="w-full h-28 object-cover" loading="lazy" />
                      {isSel && (
                        <div className="absolute top-1 right-1 bg-[#0F4C81] text-white rounded-full p-1">
                          <CheckCircle2 className="w-3.5 h-3.5" />
                        </div>
                      )}
                      {p.caption && (
                        <div className="text-[10px] text-slate-600 px-1.5 py-1 truncate bg-white text-left" title={p.caption}>{p.caption}</div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setPullOpen(false)}>Cancel</Button>
              <Button
                type="button"
                onClick={confirmPullPhotos}
                disabled={pulling || Object.values(pullSelected).every(v => !v)}
                className="flex-1 bg-[#0F4C81] hover:bg-[#0D4270]"
                data-testid="qi-pull-confirm"
              >
                {pulling ? "Linking…" : `Link ${Object.values(pullSelected).filter(Boolean).length} photo(s)`}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}
